import moment from "moment";
import {
  getContractGroupCustomFieldsAsync,
  getContractGroupDetailAsync,
  getContractGroupListAsync,
  getContractMapOfGroupAsync,
  postContractGroupAsync,
  postContractMapOfGroupAsync,
  updateContractGroupCustomFieldsAsync,
} from "src/api/contractGroup/contractgroup-customfield-api";
import { ContractGroupListModel, ContractGroupMappingRequest, ContractGroupParam, CustomFieldModel } from "src/api/contractGroup/contractgroup-type";
import { useApiOperation } from "src/api/hooks";
import { getOrgList } from "src/api/organization/org-api";
import { MbOrg } from "src/api/organization/org-types";
import { useModal } from "src/recoil/modalState/hook";
import { downloadExcel } from "src/utils";

export const useContractGroup = () => {
  const { setBaseModal } = useModal();

  const { executeAsync: addContractGroupCustomField } = useApiOperation(updateContractGroupCustomFieldsAsync); //계약그룹 > 커스텀 필드 등록
  const { executeAsync: getContractGroupCustomField } = useApiOperation(getContractGroupCustomFieldsAsync); // 등록한 커스텀 필드 목록 조회
  const { executeAsync: getContractGroup } = useApiOperation(getContractGroupDetailAsync); // 계약그룹 상세 조회
  const { executeAsync: addContractGroup } = useApiOperation(postContractGroupAsync); // RCR14. 계약그룹 등록
  const { executeAsync: getContractMapOfGroup } = useApiOperation(getContractMapOfGroupAsync); // 계약그룹 계약매핑 조회
  const { executeAsync: postContractMapOfGroup } = useApiOperation(postContractMapOfGroupAsync); // 계약그룹 계약매핑 등록
  const { executeAsync: getOrgListApi } = useApiOperation(getOrgList); // 사업자 목록 조회
  const { executeAsync: getContractGroupList } = useApiOperation(getContractGroupListAsync); // 계약그룹 목록 조회

  // RCR13. 계약그룹 상세 조회 api
  const getContractGroupDetail = async (groupId: string) => {
    const { data } = await getContractGroup({ groupId });
    return data.data.contractGroup;
  };

  // RCR15. 계약그룹 계약매핑 조회 api
  const getContractMapOfGroupList = async (groupId: string) => {
    const { data } = await getContractMapOfGroup({ groupId });
    const contractList = data.data.contractSummaryList;
    const addableContractIds = data.data.addableContractIdList;

    const mbIds = contractList.map((item) => item.mbOrganizationId);
    const orgListData = await getOrgListApi({ id: mbIds.join(",") })
      .then(({ data: orgList }) => orgList.data.content)
      .catch(() => [] as MbOrg[]);
    const orgMap = new Map(orgListData.map((item) => [item.id, item]));

    const contractListWithOrg = contractList.map((item) => ({
      ...item,
      orgName: orgMap.get(item.mbOrganizationId)?.classifications?.includes("INDIVIDUAL")
        ? orgMap.get(item.mbOrganizationId)?.ceoName
        : orgMap.get(item.mbOrganizationId)?.name,
    }));

    return { contractListWithOrg, addableContractIds };
  };

  // RCR16. 계약그룹 계약매핑 등록 api
  const addContractMapOfGroup = (request: ContractGroupMappingRequest[]) => postContractMapOfGroup({ contractGroupMapList: request });

  // 등록된 커스텀 필드 조회 api
  const getCtGroupCustomField = async () => {
    try {
      const { data } = await getContractGroupCustomField();

      return data.data.customFieldList;
    } catch (error) {
      console.error("Custom field 조회 실패:", error);
      return [];
    }
  };

  // 커스텀 필드 등록 api
  const addCtGroupCustomField = async (request: CustomFieldModel) => {
    return await addContractGroupCustomField([request]);
  };

  // 커스텀 필드 수정 api
  const editCtGroupCustomField = async (request: CustomFieldModel | CustomFieldModel[]) => {
    return await addContractGroupCustomField(Array.isArray(request) ? request : [request]);
  };

  // 커스텀 필드 삭제 api
  const deleteCtGroupCustomField = async (request: CustomFieldModel) => {
    return await addContractGroupCustomField([request]);
  };

  // 엑셀 다운로드 함수
  const onDownloadContractGroupExcel = async (contractGroupColumns: any, queryParams: ContractGroupParam) => {
    try {
      // 현재 검색 조건으로 전체 데이터 조회 (size, page, mbOrganizationName 제거)
      const { mbOrganizationName, size, page, ...excelParams } = queryParams;

      const { data } = await getContractGroupList(excelParams);
      const contractGroups = data.data.content;

      // 조직 정보 매핑 (엑셀은 화면과 달리 모든 계약자 이름을 표기하므로 전체 org id 조회)
      let allData = contractGroups;

      // 1. 각 계약그룹의 mbOrganizationIdList 전체에서 유니크 id 수집
      const allOrgIds = [
        ...new Set(contractGroups?.flatMap((item) => item.mbOrganizationIdList || []).filter((id) => id !== undefined && id !== null)),
      ];

      if (allOrgIds.length > 0) {
        // 2. 전체 조직 정보 조회
        const { data: orgData } = await getOrgListApi({
          page: 0,
          size: allOrgIds.length,
          id: allOrgIds.join(","),
        });

        const orgList = orgData.data.content;
        const orgMap = new Map(orgList.map((org) => [org.id, org]));

        // 3. result에 조직 정보 추가
        allData =
          contractGroups?.map((item) => {
            const orgIdList = item.mbOrganizationIdList || [];

            // 조직 ID 리스트가 빈 배열이면 "-" 표시
            if (orgIdList.length === 0) {
              return {
                ...item,
                orgName: "-",
                orgDisplayName: "-",
                orgExcelName: "-",
              };
            }

            // 중복 제거한 조직 id 리스트
            const uniqueOrgIds = [...new Set(orgIdList)];

            const firstOrgId = uniqueOrgIds[0];
            const org = orgMap.get(firstOrgId);
            const remainingCount = uniqueOrgIds.length - 1; // 첫 번째 조직 제외한 나머지

            // 엑셀용: 모든 계약자 이름을 반점(, )으로 구분해 표기
            const orgExcelName = uniqueOrgIds
              .map((id) => {
                const o = orgMap.get(id);
                return o?.classifications?.includes("INDIVIDUAL") ? o?.ceoName : o?.name || o?.ceoName || "미지정";
              })
              .join(", ");

            return {
              ...item,
              orgName: org?.classifications?.includes("INDIVIDUAL") ? org?.ceoName : org?.name || "미지정",
              orgDisplayName:
                remainingCount > 0 ? `${org ? org.name || org.ceoName : "미지정"} 외 ${remainingCount}개` : org ? org.name || org.ceoName : "미지정",
              orgExcelName,
            };
          }) || [];
      }

      // react-table 컬럼의 Cell 함수를 사용해 데이터 변환
      const downloadData = allData.map((rowData) => {
        const convertedRow: any = {};

        contractGroupColumns.forEach((column: any) => {
          const accessor = column.accessor as string;

          // Header 텍스트 추출
          let headerText: string;
          if (typeof column.Header === "function") {
            // React Element에서 텍스트 추출
            const headerElement = column.Header();
            if (headerElement && typeof headerElement === "object" && "props" in headerElement) {
              headerText = headerElement.props.children || accessor;
            } else {
              headerText = String(headerElement);
            }
          } else {
            headerText = String(column.Header);
          }

          let cellValue: any = "-";

          // 계약자(법인명/상호): 엑셀은 전체 이름을 반점(, )으로 표기
          if (accessor === "orgDisplayName") {
            convertedRow[headerText] = (rowData as any).orgExcelName || "-";
            return;
          }

          // 커스텀 필드인 경우
          if (accessor.startsWith("fieldId_")) {
            const fieldId = parseInt(accessor.replace("fieldId_", ""));
            const inputData = rowData.inputList?.find((input) => input.fieldId === fieldId);

            if (inputData) {
              // Cell 함수 실행해서 포맷된 값 가져오기
              if (column.Cell) {
                try {
                  const cellResult = column.Cell({
                    value: inputData.inputValue,
                    row: { original: rowData },
                  } as any);

                  // React Element에서 텍스트 추출
                  if (cellResult && typeof cellResult === "object" && "props" in cellResult) {
                    if (cellResult.props.contents) {
                      cellValue = cellResult.props.contents;
                    } else if (cellResult.props.children) {
                      cellValue = cellResult.props.children;
                    } else {
                      cellValue = inputData.inputValue || "-";
                    }
                  } else {
                    cellValue = cellResult || "-";
                  }
                } catch (error) {
                  cellValue = inputData.inputValue || "-";
                }
              } else {
                cellValue = inputData.inputValue || "-";
              }
            }
          } else {
            // 일반 필드인 경우
            const rawValue = rowData[accessor as keyof ContractGroupListModel];

            if (column.Cell) {
              try {
                const cellResult = column.Cell({
                  value: rawValue,
                  row: { original: rowData },
                } as any);

                // React Element에서 텍스트 추출
                if (cellResult && typeof cellResult === "object" && "props" in cellResult) {
                  if (cellResult.props.children !== undefined) {
                    cellValue = cellResult.props.children;
                  } else if (cellResult.props.contents !== undefined) {
                    cellValue = cellResult.props.contents;
                  } else if (cellResult.props.to && rawValue) {
                    // Link 컴포넌트인 경우
                    cellValue = rawValue;
                  } else {
                    cellValue = String(rawValue || "-");
                  }
                } else {
                  cellValue = cellResult;
                }
              } catch (error) {
                cellValue = String(rawValue || "-");
              }
            } else {
              cellValue = rawValue;
            }
          }

          convertedRow[headerText] = cellValue || "-";
        });

        return convertedRow;
      });

      // 파일명 생성
      const searchCondition = queryParams.searchValue || queryParams.groupName ? "검색결과" : "전체";
      const fileName = `ctrl.room_계약그룹목록_${searchCondition}_${moment().format("YYYYMMDDHHmmss")}`;

      // 엑셀 다운로드
      await downloadExcel({
        data: downloadData,
        fileName,
        header: contractGroupColumns.map((column: any) => {
          if (typeof column.Header === "function") {
            const headerElement = column.Header();
            if (headerElement && typeof headerElement === "object" && "props" in headerElement) {
              return headerElement.props.children || column.accessor;
            } else {
              return String(headerElement);
            }
          } else {
            return String(column.Header);
          }
        }),
      });

      setBaseModal({ isOpen: true, title: "엑셀 다운로드가 완료되었습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
    } catch (error) {
      console.error("엑셀 다운로드 실패:", error);
      setBaseModal({ isOpen: true, title: "엑셀 다운로드에 실패하였습니다.", btnRightTitle: "확인", onClick: () => setBaseModal({ isOpen: false }) });
    }
  };

  return {
    getCtGroupCustomField,
    addCtGroupCustomField,
    editCtGroupCustomField,
    deleteCtGroupCustomField,
    getContractGroupDetail,
    addContractGroup,
    getContractMapOfGroupList,
    addContractMapOfGroup,
    getContractGroupList,
    onDownloadContractGroupExcel,
  };
};
